import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { Button, Paper, Menu, MenuItem } from "@material-ui/core";
import ResponsiveTable from "../table/ResponsiveTable";
import TableRowProfile from "../util/TableRowProfile";
import ConfirmationModal from "../modal/ConfirmationModal";
import Loading from "../loadingSpinner/Loading";
import { ProfileRelationType, ProfileType } from "../../types/enum";
import { Promise } from "bluebird";
import blankProfile from "../../img/hotgig-loading.png";
import image from "../../helper/image";

@inject("agentActStore", "templateStore", "userInvitationStore", "profileStore")
@observer
class ApplyToPlayTable extends Component {
    state = {
        actionAnchor: [],
        declineModalState: false,
        selectedApplication: undefined
    };

    componentDidMount() {
        this.props.agentActStore.getAgentsPendingRequest();
        this.props.userInvitationStore.getMyUserInvitation(ProfileRelationType.AgentAct.ordinal);
    }

    columns = [
        { key: "actName", label: "Act Name", width: "30%", sortable: true, isString: true, addComp: true },
        { key: "actOwner", label: "Act Owner", width: "30%", sortable: true, isString: true, addComp: true },
        { key: "action", label: "", width: "40%" }
    ];

    setActionAnchor = (element, id) => {
        var tempAnchor = [];
        tempAnchor[id] = element;
        this.setState({ ...this.state, actionAnchor: tempAnchor });
    };

    unsetActionAnchor = id => {
        this.setState({ ...this.state, actionAnchor: [] });
    };

    handleOpenDeclineModal = application => {
        this.setState({
            ...this.state,
            declineModalState: true,
            selectedApplication: application
        });
    };

    handleCloseModal = () => {
        this.setState({
            ...this.state,
            declineModalState: false,
            selectedApplication: undefined
        });
    };

    handleDeclineInvitation = (invitationId, actName) => {
        this.props.userInvitationStore.declineUserInvitation(invitationId).then(() => {
            this.props.userInvitationStore.resetInvitations();
            this.props.agentActStore.resetAgentActs();
            Promise.all([
                this.props.agentActStore.getAgentsPendingRequest(),
                this.props.agentActStore.getAgentActRoster(),
                this.props.userInvitationStore.getMyUserInvitation(ProfileRelationType.AgentAct.ordinal)
            ]).then(() => { 
                this.props.templateStore.openSnackbar(actName + " has been declined.");
            });
        });
    }

    handleAcceptInvitation = (data) => {
        this.props.agentActStore.acceptInvitation(data).then(() => {
            this.props.userInvitationStore.resetInvitations();
            this.props.agentActStore.resetAgentActs();
            Promise.all([
                this.props.agentActStore.getAgentsPendingRequest(),
                this.props.agentActStore.getAgentActRoster(),
                this.props.userInvitationStore.getMyUserInvitation(ProfileRelationType.AgentAct.ordinal)
            ]).then(() => { 
                this.props.templateStore.openSnackbar(data.invitedAct + " has been accepted to your roster.");
            });
        });
    }

    handleAcceptRequest = request => {
        var actName = request.primaryProfile?.fullName || "Act";
        this.props.agentActStore.acceptRequest(request).then(() => {
            this.props.agentActStore.resetAgentActs();
            Promise.all([
                this.props.agentActStore.getAgentsPendingRequest(),
                this.props.agentActStore.getAgentActRoster(),
                this.props.userInvitationStore.getMyUserInvitation(ProfileRelationType.AgentAct.ordinal)
            ]).then(() => { 
                this.props.templateStore.openSnackbar(actName + " has been accepted to your roster.");
            });
        });
    };

    handleDeclineRequest = request => {
        var actName = request.primaryProfile?.fullName || "Act";
        this.props.agentActStore.declineRequest(request).then(() => {
            this.props.agentActStore.resetAgentActs();
            Promise.all([
                this.props.agentActStore.getAgentsPendingRequest(),
                this.props.userInvitationStore.getMyUserInvitation(ProfileRelationType.AgentAct.ordinal)
            ]).then(() => { 
                this.props.templateStore.openSnackbar(actName + " has been declined.");
            });
        });
    };

    mapApplications = () => {
        const rows = [];
        const { invitations } = this.props.userInvitationStore;
        const { pendingAgentActs } = this.props.agentActStore;

        invitations.filter(data => data.additionalData).forEach(data => {
            var row = {};

            row.actOwner = {
                newFormat: (
                    <TableRowProfile 
                        name={`${data.invitedFirstName} ${data.invitedLastName}`} 
                        avatarUrl={data.ownerImage ? data.ownerImage : blankProfile} 
                        profileId={data.invitedVenue}
                    />
                ),
                value: `${data.invitedFirstName} ${data.invitedLastName}`
            };

            row.actName = {
                newFormat: (
                    <TableRowProfile 
                        name={data.invitedAct} 
                        avatarUrl={data.actImage ? data.actImage : blankProfile} 
                        profileId={data.additionalData}
                    />
                ),
                value: data.invitedAct
            };

            row.action = (
                <Fragment>
                    <Button 
                        size="small" 
                        style={{ 
                            marginRight: 8,
                            border: "1px solid #f44336",
                            color: "#f44336"
                        }}
                        onClick={() => this.handleDeclineInvitation(data.id, data.invitedAct)}
                    >
                        Decline
                    </Button>
                    <Button 
                        size="small" 
                        style={{ 
                            background: "#4caf50",
                            color: "white",
                            marginRight: 8
                        }}
                        onClick={() => this.handleAcceptInvitation(data)}
                    >
                        Accept
                    </Button>
                </Fragment>
            );

            row.style = {
                backgroundColor: "#fff8e1"
            };

            rows.push(row);
        });

        pendingAgentActs.forEach(data => {
            var act = data.primaryProfile.type === ProfileType.Act.ordinal ? data.primaryProfile : data.secondaryProfile;
            var sentByAct = data.primaryProfile.type === ProfileType.Act.ordinal;

            if (!sentByAct) return;

            var row = {};

            row.actName = {
                newFormat: (
                    <TableRowProfile 
                        profileId={act.myMusician?.id} 
                        name={act.fullName} 
                        avatarUrl={image.getProfileImage(act)}
                    />
                ),
                value: act.fullName
            };

            row.actOwner = {
                newFormat: (
                    <TableRowProfile 
                        profileId={act.myMusician?.id} 
                        name={act.myMusician?.fullName}
                        avatarUrl={image.getProfileImage(act.myMusician)}
                    />
                ),
                value: act.myMusician?.fullName || ""
            };

            row.action = (
                <Fragment>
                    <Button 
                        size="small" 
                        style={{ 
                            marginRight: 8,
                            border: "1px solid #f44336",
                            color: "#f44336"
                        }}
                        onClick={() => this.handleDeclineRequest(data)}
                    >
                        Decline
                    </Button>
                    <Button 
                        size="small" 
                        style={{ 
                            background: "#4caf50",
                            color: "white"
                        }}
                        onClick={() => this.handleAcceptRequest(data)}
                    >
                        Accept
                    </Button>
                </Fragment>
            );

            row.style = {
                backgroundColor: "#fff8e1"
            };

            rows.push(row);
        });

        return rows;
    };

    render() {
        const { isLoadingUserInvitations } = this.props.userInvitationStore;
        const { isLoadingPendingActs } = this.props.agentActStore;
        const rows = this.mapApplications();

        return (
            <Fragment>
                <Paper style={{ padding: 30, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                    <p style={{ fontSize: 14, color: "#666", marginBottom: 20 }}>
                        Acts who have applied to join your roster - review and respond
                    </p>
                    
                    <div style={{ position: "relative" }}>
                        <Loading showed={isLoadingUserInvitations || isLoadingPendingActs} />
                        {rows.length > 0 ? (
                            <ResponsiveTable
                                columns={this.columns}
                                rows={rows}
                                showToolbar={true}
                                title="Applications"
                                pagination={rows.length > 5}
                                sortable={true}
                            />
                        ) : (
                            <div style={{ textAlign: "center", padding: "60px 40px", color: "#999" }}>
                                <i className="fas fa-inbox" style={{ fontSize: 48, marginBottom: 16, color: "#ddd" }}></i>
                                <h3 style={{ color: "#666", marginBottom: 8 }}>No Pending Applications</h3>
                                <p>You don't have any act applications to review at this time.</p>
                            </div>
                        )}
                    </div>
                </Paper>
            </Fragment>
        );
    }
}

export default ApplyToPlayTable;
